export class GlobalHelper {
  /**
   * Erstellt eine tiefe Kopie eines Objektes
   * @param {T} obj
   * @return {T}
   */
  public static deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Gibt zurück ob übergebener Wert ein Objekt ist
   * - Prüft, dass der übergebene Wert vom Typ 'object' ist
   * - Prüft, dass der übergebene Wert nicht null ist (denn typeof null === 'object')
   * - Prüft, dass der übergebene Wert kein Array ist (denn typeof [] === 'object')
   * @param obj Das Objekt das geprüft werden soll
   * @returns boolean - Gibt true zurück, wenn es sich um ein Objekt handelt
   * @see https://hackernoon.com/understanding-isobject-method-in-javascript
   */
  public static isObject(obj: any): boolean {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
  };

  /**
   * Vergleicht zwei Objekte auf Gleichheit (oberflächlich)
   * - Vergleicht, wenn beide Werte keine Objekte sind, direkt die Werte
   * - Prüft, ob nur ein Wert ein Objekt ist
   * - Prüft, ob die Anzahl der Keys in beiden Objekten gleich ist
   * - Prüft, ob die Werte der Keys in beiden Objekten gleich sind
   *   - Direkter Vergleich, falls die Werte der Keys keine Objekte sind
   * @param obj1 Das erste Objekt zum Vergleichen.
   * @param obj2 Das zweite Objekt zum Vergleichen.
   * @returns true, wenn die Objekte gleich sind, sonst false.
   */
  public static isEqual(obj1: any, obj2: any): boolean {

    const isObj1: boolean = GlobalHelper.isObject(obj1);
    const isObj2: boolean = GlobalHelper.isObject(obj2);

    // Prüfen, ob beide Werte keine Objekte sind und direkt vergleichen
    if (!isObj1 && !isObj2) return obj1 === obj2;

    // Prüfen, ob nur ein Wert ein Objekt ist
    if (isObj1 && !isObj2) return false;

    // Keys der Objekte holen
    const keys1: string[] = Object.getOwnPropertyNames(obj1);
    const keys2: string[] = Object.getOwnPropertyNames(obj2);

    // Prüfen, ob beide Objekte die gleiche Anzahl an Keys haben
    if (keys1.length !== keys2.length) return false;

    // Prüfen, ob die Werte der Keys in beiden Objekten gleich sind
    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Gibt immer einen String zurück. Leerer String wenn Null
   * @param value
   * @returns
   */
  public static nullToEmptyString(value: string | null): string {
    return value || '';
  }

  /**
   * Markiert ein Element als gelöscht oder löscht es aus dem Array
   * @param array
   * @param index
   */
  public static markForDestroyOrSplice(array: any[], index: number) {
    if (array[index].id > 0) {
      array[index]._destroy = true;
    } else {
      array.splice(index, 1);
    }
  }

  /**
   * Gibt zurück ob eine Zahl 0, null oder undefined ist
   * @param {number} value
   * @return {boolean}
   */
  public static isZero(value: number | null | undefined): boolean {
    return value === 0 || this.isNullOrUndefined(value);
  }

  public static isNumber(value: any): boolean {
    if (value === null || value === undefined) return false;
    value = Number(value);
    return !Number.isNaN(value) && (value === 0 || value > 0 || value < 0);
  }

  /**
   * Prüft ob value null oder undefined ist
   * @param value
   * @returns
   */
  public static isNullOrUndefined(value: any): boolean {
    return value === null || value === undefined;
  }

  /**
   * Entfernt das Feld ID aus den Objekten
   * @param objects
   * @returns
   */
  public static removeIdFromObjects<T>(objects: T[]): T[] {
    return objects.map((obj) => {
      delete (obj as { id?: unknown }).id;
      return obj;
    });
  }

  /**
   * Vergleich von zwei Iso Dates.
   * aDateIso >= bDateIso
   * @param {string} aDateIso
   * @param {string} bDateIso
   * @return {boolean}
   */
  public static dateGreaterEquals(aDateIso: string, bDateIso: string): boolean {
    const d1 = new Date(aDateIso);
    const d2 = new Date(bDateIso);

    return d1.getTime() >= d2.getTime();
  }

  /**
   * Substrahiert Datum A von Datum B auf Zeitebene
   * Hilfsfunktion zum Sortieren
   * @param {string | Date} aDateIso
   * @param {string | Date} bDateIso
   * @return {boolean}
   */
  public static subtractDates(
    aDateIso: string | Date,
    bDateIso: string | Date
  ): number {
    const dateA = new Date(aDateIso);
    const dateB = new Date(bDateIso);
    return dateB.getTime() - dateA.getTime();
  }

  /**
   * Einen moment Warten und ein Schläfchen machen zZzzZZz
   * @param {number} time
   */
  public static async sleep(time: number) {
    await new Promise((r) => setTimeout(r, time));
  }

  /**
   * Base64 in ein File umwandeln
   * @param url
   * @param filename
   * @returns
   */
  public static async base64ToFile(
    url: string,
    filename: string
  ): Promise<File> {
    const res = await fetch(url);
    const blob = await res.blob();
    return new File([blob], filename, { type: 'image/png' });
  }

  /**
   * Logs a deep copy of the data
   * @param data to log
   */
  public static deepLogger(data: any, info: string) {
    console.debug(
      `[DEBUG]: ${info}`,
      data ? JSON.parse(JSON.stringify(data)) : data
    );
  }
}
